import { Query, SearchResult, SearchResultPage } from './dtos';
import { filter, get, gt, gte, lt, lte, orderBy } from 'lodash-es';
import { search } from 'ss-search';
import { WhereFilterOp } from 'firebase/firestore';

export abstract class QueryService {
  static applyQuery<T>(dataToFilter: T[], q: Query, searchFields: string[] = []): SearchResult<T> {
    let results = [...dataToFilter];
    results = this.applySearchPhrase(results, q, searchFields);
    results = this.applySort(results, q);
    return this.paginateQuery<T>(q, results);
  }

  static searchCollection<T>(dataToFilter: T[], q: Query, searchFields?: string[]): SearchResult<T> {
    const results = this.applyFilters(dataToFilter, q);
    return this.applyQuery(results, q, searchFields);
  }

  static paginateQuery<T>(q: Query, results: T[], totalResultCnt = 0): SearchResult<T> {
    let from = q.from || 0;
    if (from > 0 && from === results.length) {
      from -= q.size;
    }

    const size = q.size || 25;
    const totalElements = totalResultCnt || results.length;
    const result = results.slice(from, from + size);

    const pageInx = Math.floor(from / size);
    const page: SearchResultPage = {
      page: pageInx,
      number: pageInx,
      size,
      totalElements,
      totalPages: Math.ceil(totalElements / size),
    };

    console.log(`Page result from query:`, q, page);
    return { page, result };
  }

  static applySort<T>(dataToFilter: T[], q: Query): T[] {
    if (q.sort) {
      const sortKeys = Object.keys(q.sort);
      const sortValues = Object.values(q.sort);

      console.log(`Sorting data on ${sortKeys.join(',')}`);
      const sortKeysNullHandlers = sortKeys.map((key) => {
        return (o: unknown) => {
          // Default to -1 because according to lodash, null is the biggest, '' is > 0 ...
          let value = get(o, key, '-1');
          if (typeof value === 'string') {
            value = value.toLowerCase();
          }
          // default value from get will not be given if null
          else if (value === null) {
            value = '-1';
          }
          return value;
        };
      });

      return orderBy<T>(dataToFilter, sortKeysNullHandlers, sortValues);
    }

    return dataToFilter;
  }

  private static applySearchPhrase<T>(dataToFilter: T[], q: Query, searchFields?: string[]): T[] {
    if (q.searchPhrase && dataToFilter.length && searchFields?.length) {
      console.log(`Searching for ${q.searchPhrase} in ${searchFields.join(',')}`);
      return search<T>(dataToFilter, searchFields, q.searchPhrase) as unknown as T[];
    }

    return dataToFilter;
  }

  private static applyFilters<T>(dataToFilter: T[], query: Query): T[] {
    let filteredData: T[] = dataToFilter;

    const filterKeys = Object.keys(query.filters || {});
    if (filterKeys.length) {
      filteredData = filterKeys.reduce((results: T[], filterKey: string) => {
        return filter<T>(results, [filterKey, query.filters[filterKey]]);
      }, filteredData);
    }

    if (query.filtersOps?.length) {
      filteredData = this.applyFilterOps(filteredData, query);
    }

    if (query.filterFn) {
      filteredData = filter<T>(filteredData, query.filterFn);
    }

    return filteredData;
  }

  private static applyFilterOps<T>(dataToFilter: T[], query: Query): T[] {
    return query.filtersOps.reduce((results: T[], filterOp) => {
      if (!filterOp.value) {
        return results;
      }

      const { field, op, value }: { field: string; op: WhereFilterOp; value: unknown } = filterOp;
      switch (op) {
        case '==':
          return filter<T>(results, [field, value]);
        case 'in':
          return filter<T>(results, (record: any) => (value as []).some((v) => v === get(record, field)));
        case '>=':
          return filter<T>(results, (record: any) => get(record, field) !== null && gte(get(record, field), value));
        case '<=':
          return filter<T>(results, (record: any) => get(record, field) !== null && lte(get(record, field), value));
        case '>':
          return filter<T>(results, (record: any) => get(record, field) !== null && gt(get(record, field), value));
        case '<':
          return filter<T>(results, (record: any) => get(record, field) !== null && lt(get(record, field), value));
        case '!=':
          return filter<T>(results, (record: any) => get(record, field) !== value);
        default:
          throw new Error('Operator not implemented');
      }
    }, dataToFilter);
  }

  search<T>(dataToFilter: T[], q: Query, searchFields?: string[]): SearchResult<T> {
    return QueryService.searchCollection(dataToFilter, q, searchFields);
  }
}
